import BackupAlert from '../../types/backup/BackupAlert';
import ChartPlotLine from '../../types/ChartPlotLine';
import { SEVERITY_LABEL } from '../../helpers/utils';
import { formatBackupTime } from './backupUtils';

// Same marker and hover panel styling as the activity charts. Escape all
// server-provided text before putting it in the overlay's HTML.
const escapeHtml = (value: string) => value.replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]!));
const colour = (severity: number) => ({ 0: 'green', 1: 'red', 2: 'amber', 3: 'blue' }[severity] || 'red');
const icon = (severity: number) => `fal ${colour(severity)}-icon chart-icon fa-exclamation-triangle fa-fw`;

export function backupAlertPlotLines(alerts: BackupAlert[], min: number, max: number): ChartPlotLine[] {
    const groups = new Map<number, BackupAlert[]>();
    alerts.forEach(alert => {
        const time = Date.parse(alert.timeslice);
        if (time >= min && time <= max) {
            groups.set(time, [...(groups.get(time) ?? []), alert]);
        }
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a - b).map(([time, events]) => {
        const active = events.filter(event => event.severity > 0);
        const severity = active.length ? Math.min(...active.map(event => event.severity)) : 0;
        const details = events.map(event => {
            const units = event.units || '';
            const comparison = event.decreasing === 'true' ? 'at or below' : 'at or above';
            const reason = event.severity === 0
                ? `Recovered: ${event.value}${units} (threshold ${event.threshold}${units})`
                : `${event.value}${units}, ${comparison} threshold ${event.threshold}${units}`;
            return `<p><i class="${icon(event.severity)}"></i> <strong>${escapeHtml(event.name)}</strong>` +
                ` — ${escapeHtml(SEVERITY_LABEL[event.severity] || 'Alert')}<br/>${escapeHtml(reason)}` +
                `${event.message ? `<br/>${escapeHtml(event.message)}` : ''}</p>`;
        }).join('');
        return {
            value: time, timeValue: time, color: '#666', width: 1, dashStyle: 'dot', zIndex: 1,
            label: {
                useHTML: true, className: 'backup-alert-plot-line-label', x: -8, y: 15, rotation: 0,
                style: { color: '#666', fontWeight: 'normal' },
                text: `<div class="hover-trigger"><i class="${icon(severity)}"></i>` +
                    `${events.length > 1 ? `<sup>${events.length}</sup>` : ''}</div>` +
                    `<div class="event-tooltip"><p>${escapeHtml(formatBackupTime(events[0].timeslice))}</p>${details}</div>`,
            },
        };
    });
}
